import React from 'react';
import { useTranslation } from 'next-i18next';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Separator,
  Button,
} from '@lb-classroom/ui';
import { X } from 'lucide-react';
import classnames from 'classnames';
import RadioInputs from '@components/UI/atoms/form/RadioInputs';
import ModalMessage from '@components/UI/organisms/ModalMessage';
import type { ShowSummaryModalProps, RatingOption } from './show-summary-modal.types';

export const ShowSummaryModal = ({
  isLoading,
  isSubmitting,
  content,
  assignment,
  nextLesson,
  showRatingForm,
  existingRatingText,
  selectedRate,
  message,
  messageType,
  onRateChange,
  onSubmit,
  onClose,
}: ShowSummaryModalProps) => {
  const { t } = useTranslation('common');
  const ratingPanelBodyClass = classnames('', { loading: isSubmitting });

  const ratingOptions: RatingOption[] = [
    { value: '4', id: '0', content: t('modals.showSummary.ratingOptions.veryUseful') },
    { value: '3', id: '1', content: t('modals.showSummary.ratingOptions.useful') },
    { value: '2', id: '2', content: t('modals.showSummary.ratingOptions.littleUseful') },
    { value: '1', id: '3', content: t('modals.showSummary.ratingOptions.notUseful') },
  ];

  const ratingProps = {
    options: ratingOptions,
    className: '',
    id: 'rate-summary',
    value: selectedRate || 0,
    name: 'issue-type',
    updateHandler: (value: string) => onRateChange(parseInt(value, 10)),
    invalidLabel: true,
    onKeyDown: onSubmit,
  };

  const renderRatingContent = () => {
    if (showRatingForm) {
      return (
        <div className={ratingPanelBodyClass}>
          <div className="summary-review-wrapper">
            <h4>{t('modals.showSummary.rateTitle')}</h4>
            <p>{t('modals.showSummary.rateQuestion')}</p>
            <RadioInputs {...ratingProps} />
            <p>{t('modals.showSummary.rateDisclaimer')}</p>
          </div>
        </div>
      );
    }

    if (existingRatingText) {
      return (
        <div className="">
          <div className="summary-review-wrapper">
            <p className="mb-8">{t('modals.showSummary.ratedAs', { rating: existingRatingText })}</p>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderFooter = () => {
    if (!showRatingForm) {
      return null;
    }

    return (
      <>
        <Separator className="my-4" />
        <div className="text-right">
          <Button onClick={onSubmit} disabled={isSubmitting}>
            {t('modals.showSummary.saveRating')}
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="max-w-lg mx-auto">
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>{t('modals.showSummary.title')}</CardTitle>
          <button type="button" className="modal-close" onClick={onClose}>
            <span className="sr-only">{t('buttons.close')}</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <ModalMessage message={message} messageType={messageType} />
          <div>
            <div className="summary-content-wrapper">
              <h5 className="mb-8">{t('modals.showSummary.content')}</h5>
              <p className="mb-4">{content}</p>
              <h5 className="mb-8">{t('modals.showSummary.assignment')}</h5>
              <p className="mb-4">{assignment}</p>
              <h5 className="mb-8">{t('modals.showSummary.nextLesson')}</h5>
              <p className="mb-4">{nextLesson}</p>
            </div>
          </div>
          {renderRatingContent()}
          {renderFooter()}
        </CardContent>
      </Card>
    </div>
  );
};
